import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { BookButton } from "./BookButton";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/contact" },
];

/** Track scroll state: whether past the top and the 0..1 page scroll progress. */
const useScrollProgress = () => {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { scrolled, progress };
};

/** Liquid-glass chrome styles that intensify once the page is scrolled. */
const glassStyle = (scrolled) => ({
  borderColor: scrolled ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.5)",
  backgroundColor: scrolled ? "rgba(255,252,246,0.74)" : "rgba(255,252,246,0.46)",
  backdropFilter: scrolled ? "blur(30px) saturate(190%)" : "blur(20px) saturate(160%)",
  WebkitBackdropFilter: scrolled ? "blur(30px) saturate(190%)" : "blur(20px) saturate(160%)",
  boxShadow: scrolled
    ? "0 18px 48px rgba(43,31,23,0.16), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(255,255,255,0.25)"
    : "0 8px 26px rgba(43,31,23,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
  transition:
    "background-color 0.45s ease, box-shadow 0.45s ease, border-color 0.45s ease, backdrop-filter 0.45s ease",
});

const Wordmark = ({ className = "" }) => (
  <span className={className}>
    Bahra <span className="italic font-light">Skincare</span>
  </span>
);

const DesktopNavLinks = () => (
  <div className="hidden items-center gap-1 lg:flex">
    {NAV_ITEMS.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-${item.label.toLowerCase()}-link`}
        className={({ isActive }) =>
          `rounded-full px-4 py-2 text-sm font-medium transition-[background-color,color] duration-300 ${
            isActive
              ? "bg-[color:rgba(217,183,122,0.34)] text-[color:var(--text)] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]"
              : "text-[color:var(--text-muted)] hover:bg-[#FFFCF6]/50 hover:text-[color:var(--text)]"
          }`
        }
      >
        {item.label}
      </NavLink>
    ))}
  </div>
);

const MobileMenu = ({ open, onOpenChange }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetTrigger asChild>
      <button
        data-testid="mobile-menu-button"
        aria-label="Open menu"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-[#FFFCF6]/50 text-[color:var(--text)] backdrop-blur-md transition-colors duration-300 hover:bg-[#FFFCF6]/80"
      >
        <Menu size={20} strokeWidth={1.75} />
      </button>
    </SheetTrigger>
    <SheetContent side="right" aria-describedby={undefined} className="w-[300px] bg-[color:var(--bg)] border-l border-[color:rgba(43,31,23,0.1)]">
      <SheetTitle className="sr-only">Navigation menu</SheetTitle>
      <div className="mt-2 mb-8 font-serif text-2xl tracking-tight">
        <Wordmark />
      </div>
      <div className="flex flex-col gap-1">
        {NAV_ITEMS.map((item) => (
          <SheetClose asChild key={item.to}>
            <NavLink
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `rounded-[12px] px-3 py-3 text-base font-medium transition-colors duration-300 ${
                  isActive
                    ? "bg-[color:rgba(217,183,122,0.34)] text-[color:var(--text)]"
                    : "text-[color:var(--text-muted)] hover:bg-[color:rgba(185,163,143,0.14)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          </SheetClose>
        ))}
      </div>
      <div className="mt-8">
        <BookButton testId="mobile-book-appointment-button" className="w-full" />
      </div>
    </SheetContent>
  </Sheet>
);

export const Navbar = () => {
  const { scrolled, progress } = useScrollProgress();
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 px-3 pt-3 sm:px-4">
      <div
        className="scroll-progress"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />
      <nav
        className="mx-auto grid h-14 w-full max-w-6xl grid-cols-[1fr_auto] items-center rounded-full border px-4 sm:h-16 sm:px-5 lg:grid-cols-[1fr_auto_1fr]"
        style={glassStyle(scrolled)}
      >
        {/* Left: wordmark */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="justify-self-start font-serif text-xl tracking-tight text-[color:var(--text)] sm:text-2xl"
        >
          <Wordmark />
        </Link>

        {/* Center: nav links (desktop) */}
        <DesktopNavLinks />

        {/* Right: single Book CTA (desktop) */}
        <div className="hidden justify-self-end lg:block">
          <BookButton testId="nav-book-appointment-button" className="px-5 py-2.5" />
        </div>

        {/* Mobile nav */}
        <div className="justify-self-end lg:hidden">
          <MobileMenu open={open} onOpenChange={setOpen} />
        </div>
      </nav>
    </header>
  );
};
